import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { updateProgress, personalizeMeal } from '../../lib/api';
import { ALLERGEN_OPTIONS } from '../onboarding/MealPreferencesForm';
import {
  Sun, Dumbbell, Utensils, Brain, Moon, Star, Play, Clock,
  ChevronLeft, ChevronRight, Check, Flame, Coffee, Salad,
  Cookie, Sparkles, Tv, Music, BookOpen, Quote, Loader2,
  AlertTriangle, Wand2
} from 'lucide-react';

const mealIcons = {
  breakfast: Coffee,
  mid_morning_snack: Cookie,
  lunch: Salad,
  evening_snack: Cookie,
  dinner: Utensils,
};

const mealLabels = {
  breakfast: 'Breakfast',
  mid_morning_snack: 'Morning Snack',
  lunch: 'Lunch',
  evening_snack: 'Evening Snack',
  dinner: 'Dinner',
};

// Simple keyword check against allergen IDs
const ALLERGEN_KEYWORDS = {
  peanuts: ['peanut'],
  tree_nuts: ['almond', 'cashew', 'walnut', 'pecan', 'pistachio', 'hazelnut', 'nut'],
  dairy: ['milk', 'cheese', 'butter', 'yogurt', 'curd', 'paneer', 'cream', 'ghee'],
  gluten: ['wheat', 'bread', 'pasta', 'flour', 'roti', 'chapati', 'gluten', 'barley', 'rye'],
  shellfish: ['shrimp', 'prawn', 'crab', 'lobster', 'oyster', 'shellfish'],
  eggs: ['egg'],
  soy: ['soy', 'tofu', 'edamame', 'tempeh'],
  sesame: ['sesame', 'tahini', 'til'],
  mushrooms: ['mushroom'],
  onion_garlic: ['onion', 'garlic'],
};

function findAllergenMatches(ingredients, allergens) {
  if (!ingredients || !allergens || !allergens.length) return [];
  const text = ingredients.join(' ').toLowerCase();
  const hits = [];
  allergens.forEach(a => {
    const kws = ALLERGEN_KEYWORDS[a] || [a.replace(/_/g, ' ').toLowerCase()];
    if (kws.some(k => text.includes(k))) {
      const label = ALLERGEN_OPTIONS.find(o => o.id === a)?.label || a;
      hits.push(label);
    }
  });
  return hits;
}

export default function ProgramDayView({ dayData, programId, programName, totalDays, onBack, onDayChange, currentDayNum }) {
  const { deviceId, preferences } = useAuth();
  const [completedItems, setCompletedItems] = useState(new Set());
  const [savingItems, setSavingItems] = useState(new Set());
  const [personalized, setPersonalized] = useState({});        // { [mealKey]: meal }
  const [personalizing, setPersonalizing] = useState(null);    // mealKey
  const [personalizeError, setPersonalizeError] = useState('');

  if (!dayData) return null;

  const prefs = preferences?.meal_preferences || preferences || {};
  const userAllergens = prefs.allergens || [];
  const userCuisines = prefs.cuisines || [];
  const userDietary = prefs.dietary || '';
  const hasPrefs = !!(userDietary || userCuisines.length || userAllergens.length);

  const handlePersonalize = async (mealKey, originalMeal) => {
    setPersonalizing(mealKey);
    setPersonalizeError('');
    try {
      const res = await personalizeMeal({
        device_id: deviceId,
        meal_slot: mealKey,
        original_meal: originalMeal,
        dietary: userDietary,
        cuisines: userCuisines,
        allergens: userAllergens,
      });
      if (res.data?.meal) {
        setPersonalized(prev => ({ ...prev, [mealKey]: res.data.meal }));
      } else {
        setPersonalizeError('Could not generate a personalized meal.');
      }
    } catch (e) {
      setPersonalizeError('Personalization failed. Please try again.');
    }
    setPersonalizing(null);
  };

  const toggleComplete = async (id) => {
    const wasCompleted = completedItems.has(id);
    // Optimistic update
    setCompletedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

    // Persist to backend
    setSavingItems(prev => new Set([...prev, id]));
    try {
      await updateProgress({
        device_id: deviceId,
        program_id: programId,
        day: currentDayNum,
        activity_type: id,
        completed: !wasCompleted,
      });
    } catch {
      // Silent fail - local state already updated
    }
    setSavingItems(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  };

  const totalActivities = (dayData.workout ? 1 : 0) + Object.keys(dayData.meals || {}).length + (dayData.meditation ? 1 : 0) + (dayData.evening_routine?.length || 0);
  const completedCount = completedItems.size;
  const progressPct = totalActivities > 0 ? Math.round((completedCount / totalActivities) * 100) : 0;

  return (
    <div className="max-w-3xl mx-auto overflow-hidden" data-testid="program-day-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <button onClick={onBack} className="flex items-center gap-1 text-sm shrink-0" style={{ color: 'var(--primary)' }} data-testid="day-view-back">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <span className="text-xs font-medium truncate ml-2" style={{ color: 'var(--muted-fg)', fontFamily: 'Manrope' }}>
          {programName}
        </span>
      </div>

      {/* Day Navigation */}
      <div className="flex items-center justify-between mb-6 gap-2">
        <button onClick={() => onDayChange(currentDayNum - 1)} disabled={currentDayNum <= 1}
          className="w-9 h-9 rounded-full border flex items-center justify-center disabled:opacity-30 transition-all hover:shadow-sm shrink-0"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="prev-day">
          <ChevronLeft className="w-4 h-4" style={{ color: 'var(--text)' }} />
        </button>
        <div className="text-center flex-1 min-w-0">
          <h1 className="text-lg sm:text-2xl font-bold truncate" style={{ fontFamily: 'Manrope', color: 'var(--text)' }} data-testid="day-title">
            {dayData.title || `Day ${currentDayNum}`}
          </h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--muted-fg)' }}>Day {currentDayNum} of {totalDays}</p>
        </div>
        <button onClick={() => onDayChange(currentDayNum + 1)} disabled={currentDayNum >= totalDays}
          className="w-9 h-9 rounded-full border flex items-center justify-center disabled:opacity-30 transition-all hover:shadow-sm shrink-0"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="next-day">
          <ChevronRight className="w-4 h-4" style={{ color: 'var(--text)' }} />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="rounded-lg p-4 border mb-6 flex items-center gap-4"
        style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="day-progress">
        <div className="flex-1">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-xs font-semibold" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Today's Progress</span>
            <span className="text-xs font-bold" style={{ color: 'var(--primary)' }}>{completedCount}/{totalActivities}</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--muted)' }}>
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progressPct}%`, background: 'var(--primary)' }} />
          </div>
        </div>
        <div className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: progressPct === 100 ? 'var(--primary)' : 'var(--muted-fg)' }}>
          {progressPct}%
        </div>
      </div>

      {/* Preferences banner */}
      {hasPrefs && dayData.meals && (
        <div className="rounded-lg p-3 border mb-4 flex items-center gap-2.5 text-xs animate-fade-in"
          style={{ background: 'var(--primary)' + '10', borderColor: 'var(--primary)' + '25' }}
          data-testid="meal-prefs-banner">
          <Sparkles className="w-4 h-4 shrink-0" style={{ color: 'var(--primary)' }} />
          <p className="flex-1" style={{ color: 'var(--text)' }}>
            <span className="font-semibold">Your plate, your way.</span>{' '}
            <span style={{ color: 'var(--muted-fg)' }}>
              {userDietary && <>Dietary: <span className="capitalize">{userDietary.replace(/_/g, ' ')}</span>. </>}
              {userCuisines.length > 0 && <>Cuisines: {userCuisines.slice(0, 3).map(c => c.replace(/_/g, ' ')).join(', ')}{userCuisines.length > 3 ? ` +${userCuisines.length - 3}` : ''}. </>}
              {userAllergens.length > 0 && <>Avoiding: {userAllergens.length} items.</>}
            </span>
          </p>
          <a href="/onboarding?edit=true" className="shrink-0 font-semibold hover:underline" style={{ color: 'var(--primary)' }}
            data-testid="edit-prefs-link">Edit</a>
        </div>
      )}

      {/* Morning Affirmation */}
      {dayData.morning_affirmation && (
        <div className="rounded-lg p-5 mb-4 animate-fade-in relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, #879B7D 0%, #768A6C 100%)' }} data-testid="affirmation-card">
          <Quote className="w-5 h-5 text-white/40 absolute top-3 right-3" />
          <div className="flex items-center gap-2 mb-2">
            <Sun className="w-4 h-4 text-white/70" />
            <span className="text-xs uppercase tracking-widest text-white/60" style={{ fontFamily: 'Manrope' }}>Morning Affirmation</span>
          </div>
          <p className="text-white font-medium text-sm leading-relaxed italic">"{dayData.morning_affirmation}"</p>
          {dayData.wake_up_time && (
            <p className="text-white/50 text-xs mt-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Wake up: {dayData.wake_up_time}</p>
          )}
        </div>
      )}

      {/* Workout */}
      {dayData.workout && (
        <ActivityCard
          icon={Dumbbell}
          iconColor="#E2725B"
          title={dayData.workout.title}
          subtitle={`${dayData.workout.duration} · ${dayData.workout.intensity} intensity`}
          completed={completedItems.has('workout')}
          onToggle={() => toggleComplete('workout')}
          testId="workout-card"
          badge={dayData.workout.calories_burn}>
          <div className="space-y-2 mt-3">
            {(dayData.workout.exercises || []).map((ex, i) => (
              <div key={i} className="flex items-center justify-between text-xs py-1.5 border-b last:border-0" style={{ borderColor: 'var(--muted)' }}>
                <span style={{ color: 'var(--text)' }}>{ex.name}</span>
                <span style={{ color: 'var(--muted-fg)' }}>{ex.reps || ex.duration}</span>
              </div>
            ))}
          </div>
        </ActivityCard>
      )}

      {/* Meals */}
      {dayData.meals && Object.entries(dayData.meals).map(([mealKey, origMeal]) => {
        if (!origMeal) return null;
        const meal = personalized[mealKey] || origMeal;
        const MealIcon = mealIcons[mealKey] || Utensils;
        const allergenHits = findAllergenMatches(meal.ingredients, userAllergens);
        const isPersonalized = !!personalized[mealKey];
        const isPersonalizing = personalizing === mealKey;
        return (
          <ActivityCard
            key={mealKey}
            icon={MealIcon}
            iconColor="#879B7D"
            title={meal.name}
            subtitle={mealLabels[mealKey] || mealKey.replace(/_/g, ' ')}
            completed={completedItems.has(mealKey)}
            onToggle={() => toggleComplete(mealKey)}
            testId={`meal-${mealKey}`}
            badge={meal.calories}
            warning={allergenHits.length > 0 ? `Contains ${allergenHits.join(', ')}` : null}>

            {/* Personalized badge */}
            {isPersonalized && (
              <div className="flex items-center gap-1.5 mb-2 text-xs font-medium" style={{ color: 'var(--primary)' }}>
                <Sparkles className="w-3.5 h-3.5" />
                Personalized for your preferences
                <button onClick={() => setPersonalized(prev => { const n = {...prev}; delete n[mealKey]; return n; })}
                  className="ml-2 underline" data-testid={`reset-meal-${mealKey}`}>
                  Revert
                </button>
              </div>
            )}

            {/* Allergen warning */}
            {allergenHits.length > 0 && (
              <div className="flex items-start gap-2 mb-2 p-2 rounded text-xs"
                style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', color: '#B91C1C' }}
                data-testid={`allergen-warn-${mealKey}`}>
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                <span>Contains {allergenHits.join(', ')} — may clash with your preferences.</span>
              </div>
            )}

            {meal.ingredients && (
              <div className="mt-2">
                <p className="text-xs font-semibold mb-1" style={{ color: 'var(--muted-fg)', fontFamily: 'Manrope' }}>Ingredients</p>
                <div className="flex flex-wrap gap-1.5">
                  {meal.ingredients.map((ing, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded" style={{ background: 'var(--muted)', color: 'var(--muted-fg)' }}>{ing}</span>
                  ))}
                </div>
              </div>
            )}
            {meal.prep_time && (
              <p className="text-xs mt-2 flex items-center gap-1" style={{ color: 'var(--muted-fg)' }}>
                <Clock className="w-3 h-3" /> Prep: {meal.prep_time}
              </p>
            )}
            {meal.why_matches && (
              <p className="text-xs mt-2 italic" style={{ color: 'var(--primary)' }}>{meal.why_matches}</p>
            )}

            {/* Personalize button */}
            {hasPrefs && (
              <button onClick={() => handlePersonalize(mealKey, origMeal)} disabled={isPersonalizing}
                className="mt-3 w-full py-2 rounded-lg font-medium text-xs transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-1.5"
                style={{ background: 'var(--primary)' + '10', color: 'var(--primary)', border: '1px solid var(--primary)' + '30' }}
                data-testid={`personalize-meal-${mealKey}`}>
                {isPersonalizing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                {isPersonalizing ? 'Personalizing...' : (isPersonalized ? 'Get another option' : 'Personalize this meal')}
              </button>
            )}
          </ActivityCard>
        );
      })}

      {personalizeError && (
        <p className="text-xs text-center mb-3" style={{ color: '#DC2626' }} data-testid="personalize-error">{personalizeError}</p>
      )}

      {/* Meditation */}
      {dayData.meditation && (
        <ActivityCard
          icon={Brain}
          iconColor="#9B59B6"
          title={dayData.meditation.title}
          subtitle={`${dayData.meditation.duration} · ${dayData.meditation.type || 'Guided'}`}
          completed={completedItems.has('meditation')}
          onToggle={() => toggleComplete('meditation')}
          testId="meditation-card">
          {dayData.meditation.description && (
            <p className="text-xs mt-2 leading-relaxed" style={{ color: 'var(--muted-fg)' }}>{dayData.meditation.description}</p>
          )}
        </ActivityCard>
      )}

      {/* Evening Routine */}
      {dayData.evening_routine && dayData.evening_routine.length > 0 && (
        <div className="rounded-lg p-5 border mb-4 animate-fade-in"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="evening-routine">
          <div className="flex items-center gap-2 mb-3">
            <Moon className="w-5 h-5" style={{ color: '#4D96FF' }} />
            <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Evening Routine</h3>
            {dayData.sleep_time && (
              <span className="text-xs ml-auto" style={{ color: 'var(--muted-fg)' }}>Sleep by {dayData.sleep_time}</span>
            )}
          </div>
          <div className="space-y-2">
            {dayData.evening_routine.map((item, i) => {
              const itemId = `evening-${i}`;
              const isDone = completedItems.has(itemId);
              const text = typeof item === 'string' ? item : item.name || item.title || JSON.stringify(item);
              return (
                <button key={i} onClick={() => toggleComplete(itemId)}
                  className="w-full flex items-center gap-3 p-2.5 rounded-lg text-left transition-all text-sm"
                  style={{ background: isDone ? 'var(--primary)' + '08' : 'transparent' }}
                  data-testid={`evening-item-${i}`}>
                  <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0"
                    style={{ borderColor: isDone ? 'var(--primary)' : 'var(--border)', background: isDone ? 'var(--primary)' : 'transparent' }}>
                    {isDone && <Check className="w-3 h-3 text-white" />}
                  </div>
                  <span style={{ color: 'var(--text)', textDecoration: isDone ? 'line-through' : 'none', opacity: isDone ? 0.6 : 1 }}>{text}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Daily Challenge */}
      {dayData.daily_challenge && (
        <div className="rounded-lg p-5 border mb-4 animate-fade-in"
          style={{ background: 'linear-gradient(135deg, var(--surface) 0%, #F2EFE9 100%)', borderColor: 'var(--accent)' + '40' }} data-testid="daily-challenge">
          <div className="flex items-center gap-2 mb-2">
            <Flame className="w-5 h-5" style={{ color: 'var(--accent)' }} />
            <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Daily Challenge</h3>
          </div>
          <p className="text-sm" style={{ color: 'var(--text)' }}>{dayData.daily_challenge}</p>
        </div>
      )}

      {/* Entertainment */}
      {dayData.entertainment && (
        <div className="rounded-lg p-5 border mb-4 animate-fade-in"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="entertainment-card">
          <div className="flex items-center gap-2 mb-3">
            <Tv className="w-5 h-5" style={{ color: '#3498DB' }} />
            <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>Wind Down</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {dayData.entertainment.movie && (
              <div className="p-3 rounded-lg" style={{ background: 'var(--bg)' }}>
                <p className="text-xs font-semibold mb-0.5" style={{ color: 'var(--muted-fg)' }}>Movie</p>
                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{dayData.entertainment.movie.title}</p>
                <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>{dayData.entertainment.movie.platform}</p>
              </div>
            )}
            {dayData.entertainment.show && (
              <div className="p-3 rounded-lg" style={{ background: 'var(--bg)' }}>
                <p className="text-xs font-semibold mb-0.5" style={{ color: 'var(--muted-fg)' }}>Show</p>
                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{dayData.entertainment.show.title}</p>
                <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>{dayData.entertainment.show.platform}</p>
              </div>
            )}
            {dayData.entertainment.podcast && (
              <div className="p-3 rounded-lg" style={{ background: 'var(--bg)' }}>
                <p className="text-xs font-semibold mb-0.5" style={{ color: 'var(--muted-fg)' }}>Podcast</p>
                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{dayData.entertainment.podcast.title}</p>
                <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>{dayData.entertainment.podcast.host}</p>
              </div>
            )}
            {dayData.entertainment.spotify_playlist && (
              <div className="p-3 rounded-lg" style={{ background: 'var(--bg)' }}>
                <p className="text-xs font-semibold mb-0.5" style={{ color: 'var(--muted-fg)' }}>Playlist</p>
                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>{dayData.entertainment.spotify_playlist.name}</p>
                <p className="text-xs" style={{ color: 'var(--muted-fg)' }}>{dayData.entertainment.spotify_playlist.mood}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Motivation Quote */}
      {dayData.motivation_quote && (
        <div className="rounded-lg p-5 border mb-4 text-center animate-fade-in"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }} data-testid="motivation-quote">
          <Sparkles className="w-5 h-5 mx-auto mb-2" style={{ color: 'var(--accent)' }} />
          <p className="text-sm italic leading-relaxed" style={{ color: 'var(--text)' }}>"{dayData.motivation_quote}"</p>
        </div>
      )}
    </div>
  );
}

function ActivityCard({ icon: Icon, iconColor, title, subtitle, completed, onToggle, testId, badge, warning, children }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-lg border mb-4 overflow-hidden animate-fade-in transition-all"
      style={{ background: 'var(--surface)', borderColor: completed ? 'var(--primary)' + '50' : (warning ? '#FCA5A5' : 'var(--border)') }} data-testid={testId}>
      <div className="p-4 flex items-center gap-3">
        <button onClick={onToggle}
          className="w-7 h-7 rounded-full border-2 flex items-center justify-center shrink-0 transition-all"
          style={{ borderColor: completed ? 'var(--primary)' : 'var(--border)', background: completed ? 'var(--primary)' : 'transparent' }}
          data-testid={`${testId}-toggle`}>
          {completed && <Check className="w-4 h-4 text-white" />}
        </button>
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 relative"
          style={{ background: `${iconColor}15` }}>
          <Icon className="w-5 h-5" style={{ color: iconColor }} />
          {warning && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
              style={{ background: '#DC2626' }} title={warning}>
              <AlertTriangle className="w-2.5 h-2.5 text-white" />
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm truncate" style={{ fontFamily: 'Manrope', color: 'var(--text)', textDecoration: completed ? 'line-through' : 'none', opacity: completed ? 0.6 : 1 }}>{title}</p>
          <p className="text-xs" style={{ color: warning ? '#DC2626' : 'var(--muted-fg)' }}>{warning || subtitle}</p>
        </div>
        {badge && (
          <span className="text-xs px-2 py-1 rounded shrink-0" style={{ background: 'var(--muted)', color: 'var(--muted-fg)' }}>{badge}</span>
        )}
        {children && (
          <button onClick={() => setExpanded(!expanded)} className="shrink-0"
            style={{ color: 'var(--muted-fg)' }} data-testid={`${testId}-expand`}>
            {expanded ? <ChevronLeft className="w-4 h-4 rotate-90" /> : <ChevronRight className="w-4 h-4 rotate-90" />}
          </button>
        )}
      </div>
      {expanded && children && (
        <div className="px-4 pb-4 pt-0 border-t animate-fade-in" style={{ borderColor: 'var(--muted)' }}>
          {children}
        </div>
      )}
    </div>
  );
}
